// Reconstruct the rent's adjustment history backwards from the most recent
// applied increase. The last cycle is exact (the user just confirmed the
// monto). Earlier cycles are estimated by reversing the IPC/ICL/CASA_PROPIA
// formulas using the indices we have cached -- they may differ from what the
// owner actually charged if past adjustments weren't 100% index-driven.

import { parseLocalDate, calcularFactorIPC } from "./ipcCalculations";

const FREQ_MESES = {
  TRIMESTRAL: 3,
  CUATRIMESTRAL: 4,
  SEMESTRAL: 6,
  ANUAL: 12,
};

const FREQ_PREFIX = {
  TRIMESTRAL: "Trim.",
  CUATRIMESTRAL: "Cuatr.",
  SEMESTRAL: "Sem.",
  ANUAL: "Año",
};

export const nombreCiclo = (numero, frecuencia) => {
  const prefix = FREQ_PREFIX[frecuencia] || "Ciclo";
  return `${prefix} ${numero}`;
};

const isoFromParts = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Sums monthly index values over [desde, hasta) -- desde month inclusive,
// hasta month exclusive. Returns null when no months are published.
const sumarPctEntre = (indices, tipo, desde, hasta) => {
  const filtered = (indices || []).filter((i) => i.tipo === tipo);
  const res = calcularFactorIPC(filtered, desde, hasta);
  if (!res) return null;
  // For backwards reconstruction we want the actually-published sum so we
  // don't fabricate past values that should already exist in the DB.
  return Number(res.porcentajePublicado);
};

const ratioICL = (indices, desde, hasta) => {
  const desdeIdx = indices.find(
    (i) => i.tipo === "ICL" && i.anio === desde.getFullYear() && i.mes === desde.getMonth() + 1
  );
  const hastaIdx = indices.find(
    (i) => i.tipo === "ICL" && i.anio === hasta.getFullYear() && i.mes === hasta.getMonth() + 1
  );
  if (!desdeIdx || !hastaIdx) return null;
  const desdeV = Number(desdeIdx.valor);
  const hastaV = Number(hastaIdx.valor);
  if (!desdeV || !hastaV) return null;
  return hastaV / desdeV;
};

// Returns an array of cycles, oldest first. Each item is:
//   { numero, fecha (ISO), porcentaje (string|null), valor (number|null), esEstimado (bool) }
// The last item is the just-applied adjustment (exact). Previous items are
// estimated; if data runs out, the row is included with null fields and a
// truncation flag is set.
export const calcularHistorialAjustes = ({
  contrato,
  indices,
  montoFinal,
  fechaFinal,
  porcentajeFinal,
  cantidadCiclos = 4,
}) => {
  if (!contrato || !fechaFinal || !montoFinal) return [];
  const tipo = contrato.aumento?.tipo || "IPC";
  const frec = FREQ_MESES[contrato.aumento?.frecuencia] || 4;

  const ciclos = [
    {
      fecha: fechaFinal,
      porcentaje: porcentajeFinal != null ? Number(porcentajeFinal).toFixed(2) : null,
      valor: Number(montoFinal),
      esEstimado: false,
    },
  ];

  let valorActual = Number(montoFinal);
  let fechaActual = parseLocalDate(fechaFinal) || new Date(fechaFinal);

  for (let i = 0; i < cantidadCiclos - 1; i++) {
    const fechaPrev = new Date(fechaActual);
    fechaPrev.setMonth(fechaPrev.getMonth() - frec);

    let factor = null;
    if (tipo === "ICL") {
      factor = ratioICL(indices || [], fechaPrev, fechaActual);
    } else {
      const sumaPct = sumarPctEntre(indices || [], tipo, fechaPrev, fechaActual);
      if (sumaPct != null) factor = 1 + sumaPct / 100;
    }

    if (factor && factor > 0) {
      const valorPrev = Math.round(valorActual / factor);
      const porcentajePrev = ((factor - 1) * 100).toFixed(2);
      ciclos.unshift({
        fecha: isoFromParts(fechaPrev),
        porcentaje: porcentajePrev,
        valor: valorPrev,
        esEstimado: true,
      });
      valorActual = valorPrev;
      fechaActual = fechaPrev;
    } else {
      // Out of index data -- record a placeholder cycle and stop.
      ciclos.unshift({
        fecha: isoFromParts(fechaPrev),
        porcentaje: null,
        valor: null,
        esEstimado: true,
      });
      break;
    }
  }

  ciclos.forEach((c, i) => { c.numero = i + 1; });
  return ciclos;
};
